import { Link } from 'react-router-dom'
import './Overview.css'

// Mock data - in production this would come from API
const userData = {
  name: 'Sarah',
  company: 'Acme Corp',
  activeModules: ['studio', 'payments', 'accounting']
}

const metrics = {
  studio: {
    sites: { value: '3', label: 'Live' },
    visitors: { value: '12.8K', change: '+18.2%', changeLabel: 'vs last week' },
    conversions: { value: '3.2%', label: 'Avg conversion' }
  },
  payments: {
    todayRevenue: { value: '$2,847.00', change: '+12.4%', changeLabel: 'vs yesterday' },
    mtdRevenue: { value: '$48,291.00', change: '+8.2%', changeLabel: 'vs last month' },
    outstanding: { value: '$3,420.00', count: '4 invoices' }
  },
  accounting: {
    cashPosition: { value: '$124,500.00', label: 'Across 2 accounts' },
    expenses: { value: '$31,200.00', change: '+3.1%', changeLabel: 'vs last month' },
    profitMargin: { value: '42.3%', label: 'Month to date' }
  },
  relay: {
    conversations: { value: '0', label: 'Open' },
    avgResponse: { value: '—', label: 'Response time' },
    satisfaction: { value: '—', label: 'CSAT score' }
  },
  people: {
    employees: { value: '0', label: 'Active' },
    nextPayroll: { value: '—', label: 'Next run' },
    ptoRequests: { value: '0', label: 'Pending' }
  },
  infrastructure: {
    projects: { value: '4', label: '2 deploying' },
    bandwidth: { value: '48.2 GB', cost: '$12.40 usage' },
    uptime: { value: '99.98%', label: 'No incidents' }
  }
}

// Modules in priority order: Studio → Chekkouts → Keeply → Relay → People → Namelyft
const modules = [
  {
    id: 'studio',
    name: 'AI App Builder',
    subtitle: 'Studio',
    description: 'Build apps with AI in real-time.',
    status: 'active',
    stat: '3 live projects',
    color: 'studio'
  },
  {
    id: 'payments',
    name: 'Payments',
    subtitle: 'Chekkouts',
    description: 'Accept payments, manage checkouts, and track transactions.',
    status: 'active',
    stat: '142 transactions this week',
    color: 'payments'
  },
  {
    id: 'accounting',
    name: 'Accounting',
    subtitle: 'Keeply',
    description: 'Track expenses, manage books, and generate reports.',
    status: 'active',
    stat: 'Books up to date',
    color: 'accounting'
  },
  {
    id: 'relay',
    name: 'Chat & Support',
    subtitle: 'Relay',
    description: 'Live chat, customer support, and conversation management.',
    status: 'inactive',
    stat: 'Starting at $19/month',
    color: 'relay'
  },
  {
    id: 'people',
    name: 'HR & Payroll',
    subtitle: 'People',
    description: 'Employee management, payroll, and time tracking.',
    status: 'inactive',
    stat: 'Starting at $6/employee',
    color: 'people'
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure',
    subtitle: 'Namelyft',
    description: 'Host applications, manage domains, and deploy projects.',
    status: 'inactive',
    stat: 'Starting at $0/month',
    color: 'infrastructure'
  }
]

const activities = [
  { id: 1, type: 'payment', message: 'Payment received · $450.00 from Widget Co', time: '10:42 AM' },
  { id: 2, type: 'invoice', message: 'Invoice sent · INV-0042 to Acme Industries', time: '10:15 AM' },
  { id: 3, type: 'deploy', message: 'Deployment completed · api.myapp.com', time: '09:30 AM' },
  { id: 4, type: 'team', message: 'Team member invited · alex@company.com', time: 'Yesterday' },
  { id: 5, type: 'expense', message: 'Expense recorded · $89.00 · Software subscription', time: 'Yesterday' }
]

const alerts = [
  {
    id: 1,
    type: 'error',
    title: 'Payment method expiring',
    message: 'Your card ending in 4242 expires next month.',
    action: 'Update payment method',
    link: '/dashboard/billing'
  },
  {
    id: 2,
    type: 'warning',
    title: 'Unreconciled transactions',
    message: '12 transactions need to be categorized in Keeply.',
    action: 'Review transactions',
    link: '/dashboard/accounting/transactions'
  }
]

function Overview() {
  const isModuleActive = (moduleId) => userData.activeModules.includes(moduleId)
  
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  return (
    <div className="overview-page">
      {/* Welcome Header */}
      <section className="welcome-section">
        <div className="welcome-content">
          <h1 className="welcome-greeting">{getGreeting()}, {userData.name}</h1>
          <p className="welcome-subtitle">
            {userData.company} · {userData.activeModules.length} modules active
          </p>
        </div>
        <div className="welcome-actions">
          <Link to="/dashboard/billing" className="btn btn-secondary">View billing</Link>
          <Link to="/dashboard/team" className="btn btn-secondary">Manage team</Link>
        </div>
      </section>

      {/* Alerts */}
      {alerts.length > 0 && (
        <section className="alerts-section">
          <h2 className="section-label">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Action Required
          </h2>
          <div className="alerts-list">
            {alerts.map(alert => (
              <div key={alert.id} className={`alert-card ${alert.type}`}>
                <div className="alert-icon">
                  {alert.type === 'error' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  )}
                  {alert.type === 'warning' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  )}
                </div>
                <div className="alert-content">
                  <span className="alert-title">{alert.title}</span>
                  <span className="alert-message">{alert.message}</span>
                </div>
                <Link to={alert.link} className="alert-action">{alert.action}</Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Key Metrics */}
      <section className="metrics-section">
        {/* Studio Metrics */}
        {isModuleActive('studio') && (
          <div className="metrics-group">
            <h2 className="section-label">
              <span className="module-dot studio"></span>
              Studio Overview
            </h2>
            <div className="metrics-grid">
              <div className="metric-card">
                <span className="metric-label">Live Projects</span>
                <span className="metric-value">{metrics.studio.sites.value}</span>
                <span className="metric-sublabel">{metrics.studio.sites.label}</span>
                <Link to="/dashboard/studio" className="metric-link">View projects</Link>
              </div>
              <div className="metric-card">
                <span className="metric-label">Total Visitors</span>
                <span className="metric-value">{metrics.studio.visitors.value}</span>
                <span className="metric-change positive">{metrics.studio.visitors.change} {metrics.studio.visitors.changeLabel}</span>
                <Link to="/dashboard/studio/analytics" className="metric-link">View analytics</Link>
              </div>
              <div className="metric-card">
                <span className="metric-label">Conversion Rate</span>
                <span className="metric-value">{metrics.studio.conversions.value}</span>
                <span className="metric-sublabel">{metrics.studio.conversions.label}</span>
                <Link to="/dashboard/studio/funnels" className="metric-link">View funnels</Link>
              </div>
            </div>
          </div>
        )}

        {/* Payments Metrics */}
        {isModuleActive('payments') && (
          <div className="metrics-group">
            <h2 className="section-label">
              <span className="module-dot payments"></span>
              Payments Overview
            </h2>
            <div className="metrics-grid">
              <div className="metric-card">
                <span className="metric-label">Today's Revenue</span>
                <span className="metric-value">{metrics.payments.todayRevenue.value}</span>
                <span className="metric-change positive">{metrics.payments.todayRevenue.change} {metrics.payments.todayRevenue.changeLabel}</span>
                <Link to="/dashboard/payments/transactions" className="metric-link">View transactions</Link>
              </div>
              <div className="metric-card">
                <span className="metric-label">MTD Revenue</span>
                <span className="metric-value">{metrics.payments.mtdRevenue.value}</span>
                <span className="metric-change positive">{metrics.payments.mtdRevenue.change} {metrics.payments.mtdRevenue.changeLabel}</span>
                <Link to="/dashboard/payments" className="metric-link">View report</Link>
              </div>
              <div className="metric-card">
                <span className="metric-label">Outstanding Invoices</span>
                <span className="metric-value">{metrics.payments.outstanding.value}</span>
                <span className="metric-sublabel">{metrics.payments.outstanding.count}</span>
                <Link to="/dashboard/payments/checkouts" className="metric-link">View invoices</Link>
              </div>
            </div>
          </div>
        )}

        {/* Accounting Metrics */}
        {isModuleActive('accounting') && (
          <div className="metrics-group">
            <h2 className="section-label">
              <span className="module-dot accounting"></span>
              Accounting Overview
            </h2>
            <div className="metrics-grid">
              <div className="metric-card">
                <span className="metric-label">Cash Position</span>
                <span className="metric-value">{metrics.accounting.cashPosition.value}</span>
                <span className="metric-sublabel">{metrics.accounting.cashPosition.label}</span>
              </div>
              <div className="metric-card">
                <span className="metric-label">Expenses (MTD)</span>
                <span className="metric-value">{metrics.accounting.expenses.value}</span>
                <span className="metric-change neutral">{metrics.accounting.expenses.change} {metrics.accounting.expenses.changeLabel}</span>
              </div>
              <div className="metric-card">
                <span className="metric-label">Profit Margin (MTD)</span>
                <span className="metric-value">{metrics.accounting.profitMargin.value}</span>
                <span className="metric-sublabel">{metrics.accounting.profitMargin.label}</span>
              </div>
            </div>
          </div>
        )}

        {/* Infrastructure Metrics (if active) */}
        {isModuleActive('infrastructure') && (
          <div className="metrics-group">
            <h2 className="section-label">
              <span className="module-dot infrastructure"></span>
              Infrastructure Overview
            </h2>
            <div className="metrics-grid">
              <div className="metric-card">
                <span className="metric-label">Active Projects</span>
                <span className="metric-value">{metrics.infrastructure.projects.value}</span>
                <span className="metric-sublabel">{metrics.infrastructure.projects.label}</span>
              </div>
              <div className="metric-card">
                <span className="metric-label">Bandwidth (MTD)</span>
                <span className="metric-value">{metrics.infrastructure.bandwidth.value}</span>
                <span className="metric-sublabel">{metrics.infrastructure.bandwidth.cost}</span>
              </div>
              <div className="metric-card">
                <span className="metric-label">Uptime (30 Days)</span>
                <span className="metric-value">{metrics.infrastructure.uptime.value}</span>
                <span className="metric-sublabel success">{metrics.infrastructure.uptime.label}</span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Modules + Activity Grid */}
      <div className="overview-grid">
        {/* Your Modules */}
        <section className="modules-section">
          <h2 className="section-title">Your Modules</h2>
          <div className="modules-grid">
            {modules.map(module => (
              <div key={module.id} className={`module-card ${module.color} ${module.status}`}>
                <div className="module-header">
                  <span className={`module-status ${module.status}`}>
                    {module.status === 'active' ? '●' : '○'} {module.name}
                  </span>
                  <span className="module-subtitle">{module.subtitle}</span>
                </div>
                <p className="module-stat">{module.stat}</p>
                {module.status === 'active' ? (
                  <Link to={`/dashboard/${module.id}`} className="module-link">
                    Open
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </Link>
                ) : (
                  <button className="module-activate">Activate</button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Recent Activity */}
        <section className="activity-section">
          <h2 className="section-title">Recent Activity</h2>
          <div className="activity-list">
            {activities.map(activity => (
              <div key={activity.id} className="activity-item">
                <div className={`activity-dot ${activity.type}`}></div>
                <div className="activity-content">
                  <span className="activity-time">{activity.time}</span>
                  <span className="activity-message">{activity.message}</span>
                </div>
              </div>
            ))}
          </div>
          <Link to="/dashboard/activity" className="activity-view-all">View all activity</Link>
        </section>
      </div>
    </div>
  )
}

export default Overview
